import { z } from 'zod';
import { companyIdSchema } from './company';
import {
  compactPromptPersona,
  promptPersonaFormSchema,
  promptPersonaSchema,
  type PromptPersonaDTO,
  type PromptPersonaFormDTO,
} from './runtime-settings';

// ─── Registres ─────────────────────────────────────────────────────────────

export const ASSISTANT_PROMPT_MODES = ['conversation', 'rag', 'webStandard', 'deep'] as const;
export const ASSISTANT_PERSONA_FIELDS = [
  'assistantIdentity',
  'toneGuidelines',
  'additionalPersonaNotes',
] as const;

export const assistantPromptModeSchema = z.enum(ASSISTANT_PROMPT_MODES, {
  error: 'Le mode de prompt assistant est invalide.',
});

export const assistantPersonaFieldSchema = z.enum(ASSISTANT_PERSONA_FIELDS, {
  error: 'Le champ de persona assistant est invalide.',
});

export const assistantPersonaFieldSourceSchema = z.enum(['company', 'global', 'default'], {
  error: 'La source du champ de persona assistant est invalide.',
});

// ─── Écriture entreprise ────────────────────────────────────────────────────

export const companyAssistantSettingsWriteSchema = z.object({
  expectedVersion: z.number({
    error: 'La version attendue est invalide.',
  }).int({ error: 'La version attendue doit être un entier.' }).nonnegative({
    error: 'La version attendue ne peut pas être négative.',
  }),
  note: z.string({ error: 'La note est requise.' })
    .trim()
    .min(1, { error: 'La note ne peut pas être vide.' })
    .max(1_000, { error: 'La note ne peut pas dépasser 1000 caractères.' }),
  set: promptPersonaSchema,
  unset: z.array(assistantPersonaFieldSchema, {
    error: 'La liste des champs à réinitialiser est invalide.',
  }).transform((keys) => [...new Set(keys)]),
}).strict().superRefine((value, ctx) => {
  if (
    !('set' in value)
    || !('unset' in value)
    || typeof value.set !== 'object'
    || value.set === null
    || !Array.isArray(value.unset)
  ) {
    return;
  }

  const setValues = value.set as object;
  const overlappingKeys: string[] = [];
  for (const key of value.unset as readonly unknown[]) {
    if (typeof key === 'string' && Object.hasOwn(setValues, key)) {
      overlappingKeys.push(key);
    }
  }
  if (overlappingKeys.length === 0) return;

  ctx.addIssue({
    code: 'custom',
    message: `Un champ ne peut pas être présent dans set et unset : ${overlappingKeys.join(', ')}.`,
    path: ['unset'],
  });
});

// ─── Lecture entreprise ─────────────────────────────────────────────────────

const assistantEffectiveModeSchema = z.object({
  persona: promptPersonaSchema,
  sources: z.record(
    assistantPersonaFieldSchema,
    assistantPersonaFieldSourceSchema,
  ),
}).strict();

export const companyAssistantSettingsReadSchema = z.object({
  company: z.object({
    id: companyIdSchema,
    name: z.string().trim().min(1),
  }).strict(),
  configured: promptPersonaSchema,
  effectiveByMode: z.record(assistantPromptModeSchema, assistantEffectiveModeSchema),
  schemaVersion: z.number().int().positive(),
  updatedAt: z.iso.datetime({
    error: 'La date de mise à jour des paramètres assistant est invalide.',
  }).optional(),
  updatedBy: z.string().trim().min(1).optional(),
  version: z.number().int().nonnegative(),
}).strict();

// ─── Aperçu entreprise (draft → preview) ───────────────────────────────────

export const companyAssistantSettingsPreviewSchema = z.object({
  configured: promptPersonaSchema,
}).strict();

export const companyPersonaFormSchema = promptPersonaFormSchema;

export function toCompanyPreviewPersona(form: PromptPersonaFormDTO): PromptPersonaDTO {
  return compactPromptPersona(form);
}

const assistantPreviewModeItemSchema = z.object({
  label: z.string().trim().min(1),
  mode: assistantPromptModeSchema,
  personaPreamble: z.string().min(1),
}).strict();

export const companyAssistantSettingsPreviewResponseSchema = z.array(
  assistantPreviewModeItemSchema,
).length(ASSISTANT_PROMPT_MODES.length);

// ─── Événements d'audit entreprise ─────────────────────────────────────────

const jsonObjectSchema = z.record(z.string(), z.unknown());

export const companyAssistantSettingEventSchema = z.object({
  actorLabel: z.string().trim().min(1),
  appRelease: z.string().trim().min(1).optional(),
  changedBy: z.string().trim().min(1).optional(),
  changedKeys: z.array(z.string().trim().min(1)),
  companyId: companyIdSchema,
  createdAt: z.iso.datetime({
    error: 'La date de l\u2019événement assistant est invalide.',
  }),
  id: companyIdSchema,
  nextValue: jsonObjectSchema,
  note: z.string().trim().min(1),
  previousValue: jsonObjectSchema,
  requestId: z.string().trim().min(1).optional(),
  versionAfter: z.number().int().nonnegative(),
  versionBefore: z.number().int().nonnegative(),
}).strict();

const assistantSettingsPaginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageCount: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().nonnegative(),
}).strict();

export const paginatedCompanyAssistantSettingEventsSchema = z.object({
  items: z.array(companyAssistantSettingEventSchema),
  meta: assistantSettingsPaginationMetaSchema,
}).strict();

export const companyAssistantSettingEventsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
}).strict();

// ─── Recherche d'entreprises (combobox superadmin) ─────────────────────────

export const companySearchQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
  search: z.string().trim().max(120).optional().transform((v) => v || undefined),
}).strict();

const companyOptionSchema = z.object({
  id: companyIdSchema,
  name: z.string().trim().min(1),
  slug: z.string().trim().min(1),
}).strict();

export const paginatedCompanyOptionsSchema = z.object({
  items: z.array(companyOptionSchema),
  meta: z.object({
    page: z.number().int().min(1),
    pageCount: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().nonnegative(),
  }).strict(),
}).strict();

// ─── Types inférés ──────────────────────────────────────────────────────────

export type AssistantPromptMode = z.infer<typeof assistantPromptModeSchema>;
export type AssistantPersonaField = z.infer<typeof assistantPersonaFieldSchema>;
export type AssistantPersonaFieldSource = z.infer<typeof assistantPersonaFieldSourceSchema>;
export type CompanyAssistantSettingsWriteDTO = z.infer<typeof companyAssistantSettingsWriteSchema>;
export type CompanyAssistantSettingsReadDTO = z.infer<typeof companyAssistantSettingsReadSchema>;
export type CompanyAssistantSettingsPreviewDTO = z.infer<
  typeof companyAssistantSettingsPreviewSchema
>;
export type CompanyPersonaFormDTO = z.infer<typeof companyPersonaFormSchema>;
export type CompanyAssistantSettingsPreviewResponseDTO = z.infer<
  typeof companyAssistantSettingsPreviewResponseSchema
>;
export type CompanyAssistantSettingEventDTO = z.infer<typeof companyAssistantSettingEventSchema>;
export type PaginatedCompanyAssistantSettingEventsDTO = z.infer<
  typeof paginatedCompanyAssistantSettingEventsSchema
>;
export type CompanySearchQueryDTO = z.infer<typeof companySearchQuerySchema>;
export type PaginatedCompanyOptionsDTO = z.infer<typeof paginatedCompanyOptionsSchema>;

// Re-export for consumers that need PromptPersonaDTO alongside assistant types
export type { PromptPersonaDTO };
